/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";
import { ChevronLeft, X, Menu } from "react-feather";
import {
  Bell,
  Calendar,
  Search,
  Settings,
  User as UserIcon,
  LogOut,
  Globe,
  Loader2,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Contact,
  Shield,
  Building2,
  Receipt,
} from "lucide-react";
import { VscDashboard } from "react-icons/vsc";
import { FaProjectDiagram } from "react-icons/fa";
import { TbZoomMoneyFilled } from "react-icons/tb";
import { get, put, logout as serviceLogout, getStoredUser, getStoredValue } from "../../utils/service";
import { assets } from "@/assets/assets";
import Drawer from "../ui/Drawer";

const softSpringEasing = "cubic-bezier(0.25, 1.1, 0.4, 1)";

const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #111827;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #374151;
    border-radius: 10px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #4B5563;
  }
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: (isOpen: boolean, manual?: boolean) => void;
  userRole?: string;
  activeSection?: string;
  setActiveSection?: (section: string) => void;
  isMobile?: boolean;
  user: {
    name: string;
    email: string;
    role: string;
    initials: string;
  };
  navigate: any;
}

interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationsResponse {
  success: boolean;
  message: string;
  data: Notification[];
}

interface SearchResult {
  id: string;
  company_name?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  source?: string;
  urgency?: string;
  budget_range?: string;
  type?: string;
  decision_makers?: string | null;
  notes?: string | null;
  current_stage?: number;
  status?: string;
  progress?: string;
  value?: string;
  location?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  currency?: string;
  first_name?: string;
  last_name?: string;
  result_type: string;
}

interface SearchResponse {
  success: boolean;
  message: string;
  data: SearchResult[];
  pagination: any;
}

interface CurrencyPreference {
  currency: string;
  country_code: string;
  flag_url: string;
}

interface CurrencyPreferenceResponse {
  success: boolean;
  message: string;
  data: CurrencyPreference;
}

interface Currency {
  currency: string;
  country_code: string;
  flag_url: string;
}

interface CurrenciesResponse {
  success: boolean;
  message: string;
  data: Currency[];
}

type ExpandedItems = {
  sales: boolean;
  projects: boolean;
  reports: boolean;
};

const Sidebar = ({
  isOpen,
  toggleSidebar,
  setActiveSection,
  isMobile = false,
  user,
  navigate,
}: SidebarProps) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<ExpandedItems>({
    sales: true,
    projects: true,
    reports: true,
  });

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCurrencyView, setShowCurrencyView] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [currencyPreference, setCurrencyPreference] =
    useState<CurrencyPreference | null>(null);
  const [availableCurrencies, setAvailableCurrencies] = useState<Currency[]>(
    []
  );
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingCurrency, setIsLoadingCurrency] = useState(true);
  const [currencySearchQuery, setCurrencySearchQuery] = useState("");
  const [isTablet, setIsTablet] = useState(false);
  const [userRole, setUserRole] = useState<string>("user");
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser && storedUser.role) {
      setUserRole(storedUser.role.toLowerCase());
    } else {
      const roleFromStorage = getStoredValue('userRole');
      if (roleFromStorage) {
        setUserRole(roleFromStorage.toLowerCase());
      }
    }
  }, []);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  useEffect(() => {
    fetchCurrencyPreference();
    fetchAvailableCurrencies();
    fetchNotifications();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".user-menu-container")) {
        setShowUserMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCurrencyPreference = async () => {
    setIsLoadingCurrency(true);
    try {
      const response = await get<CurrencyPreferenceResponse>(
        "/exchange/preference"
      );
      if (response.success) {
        setCurrencyPreference(response.data);
      }
    } catch (error) {
      console.error("Error fetching currency preference:", error);
    } finally {
      setIsLoadingCurrency(false);
    }
  };

  const fetchAvailableCurrencies = async () => {
    try {
      const response = await get<CurrenciesResponse>(
        "/exchange/currencies/flags"
      );
      if (response.success) {
        setAvailableCurrencies(response.data);
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await get<NotificationsResponse>(
        "/notifications?page=1&page_size=10"
      );
      if (response.success) {
        setNotifications(response.data);
        const unread = response.data.filter((notif) => !notif.is_read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await get<SearchResponse>(
        `/search?q=${encodeURIComponent(query)}&type=all&page=1&page_size=20`
      );

      if (response.success) {
        setSearchResults(response.data);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    if (!value.trim()) {
      setSearchResults([]);
      if (showSearchResults) {
        return;
      }
      return;
    }

    if (!showSearchResults) {
      setShowSearchResults(true);
    }

    setIsSearching(true);

    searchTimeout.current = setTimeout(() => {
      handleSearch(value);
    }, 300);
  };

  const handleCurrencySelect = async (currency: string) => {
    setSelectedCurrency(currency);
    setShowCurrencyModal(true);
  };

  const handleCurrencyChange = async () => {
    setIsUpdatingCurrency(true);
    try {
      const response = await put<CurrencyPreferenceResponse>(
        "/exchange/preference",
        {
          currency: selectedCurrency,
        }
      );

      if (response.success) {
        setCurrencyPreference(response.data);
        setShowCurrencyModal(false);
        setShowCurrencyView(false);
        window.location.reload();
      }
    } catch (error) {
      console.error("Error updating currency preference:", error);
    } finally {
      setIsUpdatingCurrency(false);
    }
  };

  const formatCurrency = (value: string) => {
    const numValue = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyPreference?.currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  };

  const handleResultClick = (_result: SearchResult) => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    setShowSearchResults(false);
    setSearchQuery("");
    toggleSidebar(false);
  };

  const SearchResultsSkeleton = () => (
    <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-4 bg-black">
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          className="p-3 border-b border-gray-800 last:border-0 animate-pulse"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-700 rounded w-1/2 mb-2"></div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="h-4 bg-gray-700 rounded w-16"></div>
                <div className="h-4 bg-gray-700 rounded w-20"></div>
              </div>
            </div>
            <div className="ml-2">
              <div className="h-4 bg-gray-700 rounded w-12"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderSearchResult = (result: SearchResult) => {
    if (result.result_type === "lead") {
      return (
        <div
          key={result.id}
          className="p-3 hover:bg-gray-800 cursor-pointer border-b border-gray-800 last:border-0 transition-colors"
          onClick={() => handleResultClick(result)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-xs font-semibold text-gray-100">
                {result.company_name}
              </h4>
              <p className="text-xs text-gray-400 mt-0.5">
                {result.contact_person}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs px-2 py-0.5 bg-primary text-white rounded">
                  {result.type?.replace("_", " ").toUpperCase()}
                </span>
                <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded">
                  {result.status?.replace("_", " ").toUpperCase()}
                </span>
              </div>
            </div>
            {result.value && (
              <div className="text-right ml-2">
                <p className="text-xs font-semibold text-gray-100">
                  {formatCurrency(result.value)}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div
          key={result.id}
          className="p-3 hover:bg-gray-800 cursor-pointer border-b border-gray-800 last:border-0 transition-colors"
          onClick={() => handleResultClick(result)}
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Bell className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-100">{result.company_name}</p>
              <p className="text-xs text-gray-400 mt-1">
                by {result.first_name} {result.last_name}
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderSearchResultsView = () => (
    <>
      <div
        className={`
        relative shrink-0 transition-all duration-300 px-3 pt-4
        ${isOpen ? "w-full" : "w-full flex justify-center"}
      `}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => {
              setShowSearchResults(false);
              setSearchQuery("");
              setSearchResults([]);
            }}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h2 className="text-xs font-semibold text-gray-100">
            Search Results
          </h2>
        </div>

        <div
          className="bg-gray-900 h-10 relative rounded-lg flex items-center transition-all duration-300 w-full mb-4"
          style={{ transitionTimingFunction: softSpringEasing }}
        >
          <div className="flex items-center justify-center shrink-0 px-1">
            <div className="size-8 flex items-center justify-center">
              {isSearching ? (
                <Loader2 size={16} className="text-gray-400 animate-spin" />
              ) : (
                <Search size={16} className="text-gray-400" />
              )}
            </div>
          </div>

          <div className="flex-1 relative transition-opacity duration-300 overflow-hidden opacity-100">
            <div className="flex flex-col justify-center size-full">
              <div className="flex flex-col gap-2 items-start justify-center pr-2 py-1 w-full">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      handleSearch(searchQuery);
                    }
                  }}
                  autoFocus
                  className="w-full bg-transparent border-none outline-none text-xs text-gray-100 placeholder:text-gray-500 leading-[20px]"
                />
              </div>
            </div>
          </div>

          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                setSearchResults([]);
              }}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors mr-1"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {isSearching ? (
        <SearchResultsSkeleton />
      ) : (
        <div className="flex-1 overflow-y-auto custom-scrollbar px-3 bg-black">
          {searchResults.length > 0 ? (
            <div>{searchResults.map(renderSearchResult)}</div>
          ) : searchQuery.trim() ? (
            <div className="p-6 text-center text-gray-500 text-xs">
              No results found for "{searchQuery}"
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500 text-xs">
              Start typing to search...
            </div>
          )}
        </div>
      )}
    </>
  );

  const handleViewAllNotifications = () => {
    navigate("/notifications");
    setActiveSection?.("notifications");
    setShowNotifications(false);
    toggleSidebar(false);
  };

  const handleLogout = () => {
    serviceLogout();
  };

  const getActivityIcon = (type: string) => {
    if (type.includes("completed") || type.includes("approved")) {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    } else if (type.includes("pending") || type.includes("updated")) {
      return <Clock className="h-4 w-4 text-orange-600" />;
    } else if (type.includes("overdue") || type.includes("delayed")) {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    } else {
      return <Bell className="h-4 w-4 text-primary" />;
    }
  };

  const getActivityBgColor = (type: string) => {
    if (type.includes("completed") || type.includes("approved")) {
      return "bg-green-50";
    } else if (type.includes("pending") || type.includes("updated")) {
      return "bg-orange-50";
    } else if (type.includes("overdue") || type.includes("delayed")) {
      return "bg-red-50";
    } else {
      return "bg-blue-50";
    }
  };

  const filteredCurrencies = availableCurrencies.filter(
    (currency) =>
      currency.currency
        .toLowerCase()
        .includes(currencySearchQuery.toLowerCase()) ||
      currency.country_code
        .toLowerCase()
        .includes(currencySearchQuery.toLowerCase())
  );

  const CurrencySkeleton = () => (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg animate-pulse">
      <div className="w-5 h-5 bg-gray-700 rounded"></div>
      <div className="w-12 h-4 bg-gray-700 rounded"></div>
    </div>
  );

  const navData = useMemo(() => {
    const allItems = [
      {
        title: "Overview",
        path: "/",
        section: "overview",
        icon: <VscDashboard className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Leads",
        path: "/sales/leads",
        section: "leads",
        icon: <TbZoomMoneyFilled className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Projects",
        path: "/projects/all",
        section: "projects",
        icon: <FaProjectDiagram className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Clients",
        path: "/clients",
        section: "clients",
        icon: <Building2 className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Invoices",
        path: "/invoices",
        section: "invoices",
        icon: <Receipt className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Users",
        path: "/users",
        section: "users",
        icon: <Contact className="h-4 w-4" />,
        roles: ["admin"],
      },
      {
        title: "Calendar",
        path: "/calendar",
        section: "calendar",
        icon: <Calendar className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
      {
        title: "Audit Logs",
        path: "/audit-logs",
        section: "audit-logs",
        icon: <Shield className="h-4 w-4" />,
        roles: ["admin"],
      },
      {
        title: "Settings",
        path: "/settings",
        section: "settings",
        icon: <Settings className="h-4 w-4" />,
        roles: ["admin", "user"],
      },
    ];

    return allItems.filter((item) =>
      item.roles.includes(userRole)
    );
  }, [userRole]);

  const toggleExpanded = (title: string) => {
    const key = title.toLowerCase().replace(/\s+/g, "-") as keyof ExpandedItems;
    if (key in expandedItems) {
      setExpandedItems((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
    }
  };

  const handleNavClick = (item: any) => {
    navigate(item.path);
    if (item.section && setActiveSection) {
      setActiveSection(item.section);
    }
    if (isMobile || isTablet) {
      toggleSidebar(false);
    }
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const dropdownVariants = {
    open: {
      opacity: 1,
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    closed: {
      opacity: 0,
      height: 0,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  const iconVariants = {
    open: { rotate: 90 },
    closed: { rotate: 0 },
  };

  const renderMainContent = () => (
    <>
      <div
        className={`
        relative shrink-0 transition-all duration-300 px-3 pt-4
        ${isOpen ? "w-full" : "w-full flex justify-center"}
      `}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div
          className={`bg-gray-900 h-10 relative rounded-lg flex items-center transition-all duration-300 ${
            isOpen ? "w-full" : "w-10 min-w-10 justify-center"
          }`}
          style={{ transitionTimingFunction: softSpringEasing }}
          ref={searchRef}
        >
          <div
            className={`flex items-center justify-center shrink-0 transition-all duration-300 ${
              isOpen ? "px-1" : "p-1"
            }`}
            style={{ transitionTimingFunction: softSpringEasing }}
          >
            <div className="size-8 flex items-center justify-center">
              <Search size={16} className="text-white" />
            </div>
          </div>

          <div
            className={`flex-1 relative transition-opacity duration-300 overflow-hidden ${
              isOpen ? "opacity-100" : "opacity-0 w-0"
            }`}
            style={{ transitionTimingFunction: softSpringEasing }}
          >
            <div className="flex flex-col justify-center size-full">
              <div className="flex flex-col gap-2 items-start justify-center pr-2 py-1 w-full">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchQuery.trim()) {
                      setShowSearchResults(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      setShowSearchResults(true);
                      handleSearch(searchQuery);
                    }
                  }}
                  className="w-full bg-transparent border-none outline-none text-xs text-primary placeholder:text-white leading-[20px]"
                  tabIndex={isOpen ? 0 : -1}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav
        className={`flex-1 py-4 px-3 space-y-1 bg-black ${
          isOpen
            ? "overflow-y-auto custom-scrollbar"
            : "overflow-hidden no-scrollbar"
        }`}
      >
        {navData.map((item) => (
          <div key={item.title} className="space-y-1">
            <div className="relative group">
              <button
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center ${
                  isOpen ? "px-3" : "justify-center px-2"
                } py-2.5 rounded-lg transition-all text-xs ${
                  isActiveRoute(item.path)
                    ? "bg-primary text-white font-medium shadow-sm"
                    : "text-white hover:bg-primary/60 hover:text-gray-100"
                }`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {isOpen && (
                  <div className="flex items-center justify-between flex-1 ml-3">
                    <span className="font-medium text-xs">{item.title}</span>
                  </div>
                )}
              </button>

              {!isOpen && !isMobile && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                  {item.title}
                </div>
              )}
            </div>
          </div>
        ))}
      </nav>
    </>
  );

  const renderCurrencyView = () => (
    <>
      <div
        className={`
        relative shrink-0 transition-all duration-300 px-3 pt-4
        ${isOpen ? "w-full" : "w-full flex justify-center"}
      `}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <button
            onClick={() => setShowCurrencyView(false)}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h2 className="text-sm font-semibold text-gray-100">
            Select Currency
          </h2>
        </div>
      </div>

      <div className="px-3 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search currencies..."
            value={currencySearchQuery}
            onChange={(e) => setCurrencySearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-100 placeholder:text-gray-500"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto custom-scrollbar px-3 pb-3 bg-black">
        {filteredCurrencies.length > 0 ? (
          <div className="space-y-1">
            {filteredCurrencies.map((currency) => (
              <button
                key={currency.currency}
                onClick={() => handleCurrencySelect(currency.currency)}
                disabled={isUpdatingCurrency}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-800 transition-colors ${
                  currencyPreference?.currency === currency.currency
                    ? "bg-gray-800 text-blue-400"
                    : "text-gray-300"
                } ${isUpdatingCurrency ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <img
                  src={currency.flag_url}
                  alt={currency.country_code}
                  className="w-6 h-6 rounded flex-shrink-0"
                />
                <div className="flex-1 text-left">
                  <p className="text-xs font-semibold">{currency.currency}</p>
                  <p className="text-xs text-gray-500">
                    {currency.country_code}
                  </p>
                </div>
                {currencyPreference?.currency === currency.currency && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 text-xs">
            No currencies found
          </div>
        )}
      </nav>
    </>
  );

  const renderNotificationsView = () => (
    <>
      <div
        className={`
        relative shrink-0 transition-all duration-300 px-3 pt-4
        ${isOpen ? "w-full" : "w-full flex justify-center"}
      `}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center gap-3 px-3 py-2">
          <button
            onClick={() => setShowNotifications(false)}
            className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={16} />
          </button>
          <h2 className="text-sm font-semibold text-gray-100">Notifications</h2>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto custom-scrollbar px-3 bg-black">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className="p-3 hover:bg-gray-800 cursor-pointer transition-colors border-b border-gray-800 last:border-0 rounded-lg mb-1"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActivityBgColor(
                    notification.type
                  )}`}
                >
                  {getActivityIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-100 mb-1">
                    {notification.title}
                  </p>
                  <p className="text-xs text-gray-400 mb-2">
                    {notification.message}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 bg-gray-700 text-gray-300 rounded">
                      {notification.type}
                    </span>
                    <span className="text-xs text-gray-600">•</span>
                    <p className="text-xs text-gray-400">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {!notification.is_read && (
                    <span className="inline-block mt-2 text-xs font-semibold px-2 py-0.5 rounded bg-primary text-white">
                      New
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-6 text-center text-gray-500 text-xs">
            No notifications
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-gray-800 bg-black">
        <button
          onClick={handleViewAllNotifications}
          className="w-full text-xs font-semibold text-blue-400 hover:bg-gray-800 py-2 rounded-lg transition-colors"
        >
          View All Notifications
        </button>
      </div>
    </>
  );

  if (isMobile || isTablet) {
    return (
      <>
        <style>{customScrollbarStyles}</style>

        <div className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-gray-800 h-16">
          <div className="flex items-center justify-between h-16 px-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => toggleSidebar(!isOpen)}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
              >
                {isOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
              <img
                src={assets.SideBarOpen}
                alt="Pelarhive Logo"
                className="h-8 w-auto object-contain"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowCurrencyView(true);
                  toggleSidebar(true);
                }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                disabled={isLoadingCurrency}
              >
                {isLoadingCurrency ? (
                  <CurrencySkeleton />
                ) : currencyPreference ? (
                  <>
                    <img
                      src={currencyPreference.flag_url}
                      alt={currencyPreference.country_code}
                      className="w-5 h-5 rounded"
                    />
                    <span className="text-xs font-semibold text-gray-100">
                      {currencyPreference.currency}
                    </span>
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-semibold text-gray-100">
                      USD
                    </span>
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setShowNotifications(true);
                  toggleSidebar(true);
                }}
                className="relative p-2 hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Bell className="h-5 w-5 text-gray-400" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs flex items-center justify-center rounded-full font-semibold">
                    {unreadCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setShowUserMenu(true);
                  toggleSidebar(true);
                }}
                className="flex items-center gap-2 hover:bg-gray-800 rounded-lg p-1.5 transition-colors"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {user.initials}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        <Drawer
          isOpen={
            isOpen && !showCurrencyView && !showNotifications && !showUserMenu
          }
          onClose={() => toggleSidebar(false)}
          position="bottom"
          heightPercentage={80}
          showCloseButton={false}
        >
          <div className="h-full flex flex-col">
            {showSearchResults
              ? renderSearchResultsView()
              : renderMainContent()}

            {!showSearchResults && (
              <div className="p-3 border-t border-gray-800 bg-black">
                <button
                  onClick={() => {
                    setShowUserMenu(true);
                    toggleSidebar(false);
                  }}
                  className="flex items-center gap-2 hover:bg-gray-800 rounded-lg p-1.5 transition-colors w-full"
                >
                  <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-white">
                      {user.initials}
                    </span>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-xs font-semibold text-gray-100">
                      {user.name}
                    </p>
                    <p className="text-xs text-gray-400">{user.role}</p>
                  </div>
                </button>
              </div>
            )}
          </div>
        </Drawer>

        <Drawer
          isOpen={showCurrencyView}
          onClose={() => {
            setShowCurrencyView(false);
            toggleSidebar(false);
          }}
          position="bottom"
          heightPercentage={80}
          showCloseButton={false}
        >
          <div className="h-full flex flex-col">{renderCurrencyView()}</div>
        </Drawer>

        <Drawer
          isOpen={showNotifications}
          onClose={() => {
            setShowNotifications(false);
            toggleSidebar(false);
          }}
          position="bottom"
          heightPercentage={80}
          showCloseButton={false}
        >
          <div className="h-full flex flex-col">
            {renderNotificationsView()}
          </div>
        </Drawer>

        <Drawer
          isOpen={showUserMenu}
          onClose={() => {
            setShowUserMenu(false);
            toggleSidebar(false);
          }}
          position="bottom"
          heightPercentage={50}
          showCloseButton={false}
        >
          <div className="h-full flex flex-col bg-black">
            <div className="p-4 border-b border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-100 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{user.role}</p>
                </div>
              </div>
            </div>
            <div className="py-1 flex-1">
              <button
                onClick={() => {
                  navigate("/settings");
                  setShowUserMenu(false);
                  toggleSidebar(false);
                }}
                className="flex w-full items-center px-4 py-2.5 text-xs text-gray-300 hover:bg-gray-800 transition-colors"
              >
                <UserIcon className="mr-3 h-4 w-4" />
                Profile Settings
              </button>
              <button
                onClick={handleLogout}
                className="flex w-full items-center px-4 py-2.5 text-xs text-red-400 hover:bg-gray-800 transition-colors border-t border-gray-700"
              >
                <LogOut className="mr-3 h-4 w-4" />
                Sign out
              </button>
            </div>
          </div>
        </Drawer>

        <AnimatePresence>
          {showCurrencyModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
              onClick={() => !isUpdatingCurrency && setShowCurrencyModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xs uppercase font-light text-gray-100 mb-1">
                      Change Currency Preference
                    </h3>
                    <p className="text-xs text-white/80">
                      This will update all monetary values across the
                      application to display in {selectedCurrency}.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">
                        Current Currency
                      </p>
                      <p className="text-xs font-semibold text-gray-100">
                        {currencyPreference?.currency || "USD"}
                      </p>
                    </div>
                    <div className="text-white text-xs">→</div>
                    <div>
                      <p className="text-xs text-gray-400 mb-1">New Currency</p>
                      <p className="text-xs font-semibold text-primary">
                        {selectedCurrency}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowCurrencyModal(false)}
                    disabled={isUpdatingCurrency}
                    className="flex-1 px-4 py-2.5 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCurrencyChange}
                    disabled={isUpdatingCurrency}
                    className="flex-1 px-4 py-2.5 text-xs font-semibold text-white bg-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isUpdatingCurrency ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Confirm Change"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <style>{customScrollbarStyles}</style>
      <aside
        className={`
        bg-black text-gray-400 fixed left-4 top-4 bottom-4 
        ${isOpen ? "w-80" : "w-16"} 
        transition-all duration-300 shadow-xl rounded-2xl border border-gray-800 flex flex-col z-40 overflow-hidden
      `}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800 bg-black rounded-t-2xl">
          <AnimatePresence mode="wait">
            {isOpen ? (
              <motion.div
                key="expanded-logo"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex items-center gap-3"
              >
                <img
                  src={assets.SideBarOpen}
                  alt="Pelarhive Logo"
                  className="h-12 w-auto object-contain"
                />
              </motion.div>
            ) : (
              <motion.div
                key="collapsed-logo"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center justify-center mx-auto"
              >
                <img
                  src={assets.SideBarClosed}
                  alt="Pelarhive"
                  className="h-8 w-auto object-contain"
                />
              </motion.div>
            )}
          </AnimatePresence>

          {isOpen && (
            <button
              onClick={() => toggleSidebar(false, true)}
              className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>

        {isOpen && (
          <AnimatePresence mode="wait">
            {showCurrencyView ? (
              <motion.div
                key="currency-view"
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {renderCurrencyView()}
              </motion.div>
            ) : showNotifications ? (
              <motion.div
                key="notifications-view"
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {renderNotificationsView()}
              </motion.div>
            ) : showSearchResults ? (
              <motion.div
                key="search-results-view"
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {renderSearchResultsView()}
              </motion.div>
            ) : (
              <motion.div
                key="main-view"
                initial={{ x: 300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -300, opacity: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex-1 flex flex-col overflow-hidden"
              >
                {renderMainContent()}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {!isOpen && renderMainContent()}

        {isOpen &&
          !showCurrencyView &&
          !showNotifications &&
          !showSearchResults && (
            <div className="p-3 border-t border-gray-800 bg-black space-y-2">
              <div className="flex items-center gap-2 justify-between">
                <button
                  onClick={() => setShowCurrencyView(true)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
                  disabled={isLoadingCurrency}
                >
                  {isLoadingCurrency ? (
                    <CurrencySkeleton />
                  ) : currencyPreference ? (
                    <>
                      <img
                        src={currencyPreference.flag_url}
                        alt={currencyPreference.country_code}
                        className="w-5 h-5 rounded"
                      />
                      <span className="text-xs font-semibold text-gray-100">
                        {currencyPreference.currency}
                      </span>
                    </>
                  ) : (
                    <>
                      <Globe className="w-4 h-4 text-gray-400" />
                      <span className="text-xs font-semibold text-gray-100">
                        USD
                      </span>
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowNotifications(true)}
                  className="relative p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Bell className="h-5 w-5 text-gray-400" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs flex items-center justify-center rounded-full font-semibold">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>

              <div className="relative user-menu-container">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 hover:bg-gray-800 rounded-lg p-1.5 transition-colors w-full"
                >
                  <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-white">
                      {user.initials}
                    </span>
                  </div>
                  <div className="text-left flex-1">
                    <p className="text-xs font-semibold text-gray-100">
                      {user.name}
                    </p>
                    <p className="text-xs text-gray-400">{user.role}</p>
                  </div>
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50"
                    >
                      <div className="p-4 border-b border-gray-700">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                            <UserIcon className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-100 truncate">
                              {user.name}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {user.email}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {user.role}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate("/settings");
                            setShowUserMenu(false);
                          }}
                          className="flex w-full items-center px-4 py-2.5 text-xs text-gray-300 hover:bg-gray-800 transition-colors"
                        >
                          <UserIcon className="mr-3 h-4 w-4" />
                          Profile Settings
                        </button>
                        <button
                          onClick={handleLogout}
                          className="flex w-full items-center px-4 py-2.5 text-xs text-red-400 hover:bg-gray-800 transition-colors border-t border-gray-700"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

        {!isOpen && (
          <div className="p-3 border-t border-gray-800 bg-black rounded-b-2xl">
            <button
              onClick={() => toggleSidebar(true, true)}
              className="w-full p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors flex items-center justify-center"
            >
              <FiChevronRight size={16} />
            </button>
          </div>
        )}
      </aside>

      <AnimatePresence>
        {showCurrencyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => !isUpdatingCurrency && setShowCurrencyModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-900 rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs uppercase font-light text-gray-100 mb-1">
                    Change Currency Preference
                  </h3>
                  <p className="text-xs text-white/80">
                    This will update all monetary values across the application
                    to display in {selectedCurrency}.
                  </p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">
                      Current Currency
                    </p>
                    <p className="text-xs font-semibold text-gray-100">
                      {currencyPreference?.currency || "USD"}
                    </p>
                  </div>
                  <div className="text-white text-xs">→</div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">New Currency</p>
                    <p className="text-xs font-semibold text-primary">
                      {selectedCurrency}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowCurrencyModal(false)}
                  disabled={isUpdatingCurrency}
                  className="flex-1 px-4 py-2.5 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCurrencyChange}
                  disabled={isUpdatingCurrency}
                  className="flex-1 px-4 py-2.5 text-xs font-semibold text-white bg-primary rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isUpdatingCurrency ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Confirm Change"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;